import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faRefresh,
    faShoppingCart,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faGlobe,
    faUserTag,
    faStickyNote,
    faIdCard,
    faSave,
    faBan,
    faUserEdit,
    faImage,
    faHashtag,
    faUserPlus,
    faExternalLinkAlt,
    faLink,
    faStore,
    faExchangeAlt,
    faBox,
    faDollarSign,
    faMoneyBillWave,
    faCreditCard,
    faWallet,
    faUniversity,
    faReceipt,
    faHistory,
    faCalculator,
    faBalanceScale,
    faPercent,
    faInfoCircle,
    faRulerCombined,
    faLayerGroup,
    faPlus,
    faMinus,
    faTrashAlt,
    faExpand,
    faCompress,
    faArrowsAlt
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createSellListTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Sales List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        salesList: 'Sales List',
        shopSales: 'Shop Sales',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search sales in shop',
        refresh: 'Refresh',
        loadingSales: 'Loading shop sales...',
        noSalesFound: 'No sales found in shop',
        saleDetails: 'Sale Details',
        close: 'Close',
        serialNo: 'S.No',
        date: 'Date',
        billNumber: 'Bill No',
        customerName: 'Customer',
        phone: 'Phone',
        items: 'Items',
        rolls: 'Rolls',
        grandTotal: 'Total',
        paidAmount: 'Paid',
        balance: 'Balance',
        paymentMethod: 'Payment',
        paymentStatus: 'Status',
        actions: 'Actions',
        addedBy: 'Added By',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        print: 'Print',
        deletedSuccess: 'Sale deleted successfully',
        deleteError: 'Error deleting sale',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        saleId: 'Sale ID',
        customerDetails: 'Customer Details',
        notProvided: 'Not provided',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        showing: 'Showing',
        of: 'of',
        sales: 'sales',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        paidFilter: 'Paid',
        unpaidFilter: 'Unpaid',
        partiallyPaidFilter: 'Partially Paid',
        filterBy: 'Filter By',
        billNoFilter: 'Bill Number',
        customerFilter: 'Customer Name',
        phoneFilter: 'Phone',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'PDF',
        addedByFilter: 'Added By',
        editSale: 'Edit Sale',
        updateSale: 'Update Sale',
        updating: 'Updating...',
        saleUpdated: 'Sale updated successfully',
        updateError: 'Error updating sale',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        dateRequired: 'Date is required',
        billNoRequired: 'Bill number is required',
        grandTotalRequired: 'Grand total is required',
        paidRequired: 'Paid amount is required',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this sale?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        loadingUserData: 'Loading shop sales data...',
        noUserRecords: 'No sales found in your shop',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalSales: 'Total Sales',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        totalAmount: 'Total Amount',
        totalByCurrency: 'Total by Currency',
        currencyBreakdown: 'Currency Breakdown',
        salesCount: 'Sales',
        paymentMethods: {
            cash: 'Cash',
            card: 'Card',
            upi: 'UPI',
            bank_transfer: 'Bank Transfer'
        },
        walkInCustomer: 'Walk-in Customer',
        notes: 'Notes',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        customerAddress: 'Address',
        optional: 'Optional',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        selectShop: 'Please select a shop to view sales',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        invoice: 'INVOICE',
        receipt: 'RECEIPT',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        cashier: 'Cashier',
        time: 'Time',
        transactionId: 'Transaction ID',
        contactUs: 'Contact Us',
        productName: 'Product',
        quantity: 'Qty',
        price: 'Price',
        total: 'Total',
        editItems: 'Edit Items',
        addProduct: 'Add Product',
        remove: 'Remove',
        updateItems: 'Update Items',
        itemName: 'Product Name',
        batchNo: 'Batch No',
        unit: 'Unit',
        color: 'Color',
        size: 'Size'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const sellListTranslator = createSellListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SellList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [sales, setSales] = useState([]);
    const [filteredSales, setFilteredSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Modal drag and resize state
    const [viewModalPosition, setViewModalPosition] = useState({ x: 0, y: 0 });
    const [editModalPosition, setEditModalPosition] = useState({ x: 0, y: 0 });
    const [viewModalSize, setViewModalSize] = useState({ width: 700, height: 'auto' });
    const [editModalSize, setEditModalSize] = useState({ width: 750, height: 'auto' });
    const [isDraggingView, setIsDraggingView] = useState(false);
    const [isDraggingEdit, setIsDraggingEdit] = useState(false);
    const [isResizingView, setIsResizingView] = useState(false);
    const [isResizingEdit, setIsResizingEdit] = useState(false);
    const [currentViewWidthMode, setCurrentViewWidthMode] = useState('medium');
    const [currentEditWidthMode, setCurrentEditWidthMode] = useState('medium');
    const [isViewModalInitialized, setIsViewModalInitialized] = useState(false);
    const [isEditModalInitialized, setIsEditModalInitialized] = useState(false);

    const dragStartView = useRef({ x: 0, y: 0 });
    const dragStartEdit = useRef({ x: 0, y: 0 });
    const viewModalRef = useRef(null);
    const editModalRef = useRef(null);
    const resizeTypeView = useRef(null);
    const resizeTypeEdit = useRef(null);
    const resizeStartView = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });
    const resizeStartEdit = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Width mode presets
    const widthModes = {
        narrow: { width: 450, label: 'Narrow', icon: faMinus },
        medium: { width: 700, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 900, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // Edit width mode presets (slightly wider)
    const editWidthModes = {
        narrow: { width: 500, label: 'Narrow', icon: faMinus },
        medium: { width: 750, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 1000, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        phonenumber: '',
        fullname: '',
        email: ''
    });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr'
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: '',
        date: '',
        bill_number: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        subtotal: '',
        tax: '',
        discount: '',
        grand_total: '',
        paid_amount: '',
        balance: '',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        currency_symbol: 'Rs',
        currency_code: 'PKR',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [billNoFilter, setBillNoFilter] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Payment methods for dropdown
    const paymentMethods = [
        { value: 'cash', label: translations.paymentMethods?.cash || 'Cash' },
        { value: 'card', label: translations.paymentMethods?.card || 'Card' },
        { value: 'upi', label: translations.paymentMethods?.upi || 'UPI' },
        { value: 'bank_transfer', label: translations.paymentMethods?.bank_transfer || 'Bank Transfer' }
    ];

    // Format currency with proper symbol from sale
    const formatCurrency = (amount, currencySymbol = null) => {
        if (!amount && amount !== 0) return '-';
        const symbol = currencySymbol || 'Rs';
        return `${symbol} ${parseFloat(amount).toFixed(2)}`;
    };

    // Get currency symbol from sale
    const getCurrencySymbol = (sale) => {
        return sale?.currency_symbol || 'Rs';
    };

    // Calculate totals grouped by currency
    const calculateCurrencyTotals = (salesData) => {
        const totals = {};
        
        salesData.forEach(sale => {
            const currency = sale.currency_code || 'PKR';
            const symbol = sale.currency_symbol || 'Rs';
            const grandTotal = parseFloat(sale.grand_total) || 0;
            const paidAmount = parseFloat(sale.paid_amount) || 0;
            const balance = parseFloat(sale.balance) || 0;
            
            if (!totals[currency]) {
                totals[currency] = {
                    currency_code: currency,
                    currency_symbol: symbol,
                    count: 0,
                    grand_total: 0,
                    paid_amount: 0,
                    balance: 0
                };
            }
            
            totals[currency].count++;
            totals[currency].grand_total += grandTotal;
            totals[currency].paid_amount += paidAmount;
            totals[currency].balance += balance;
        });
        
        return totals;
    };

    const currencyTotals = calculateCurrencyTotals(filteredSales);
    const totalSales = filteredSales.length;
    const uniqueCurrencies = Object.keys(currencyTotals).length;

    const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSales = filteredSales.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction
        });

        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    // Center modal function
    const centerViewModal = useCallback(() => {
        if (!viewModalRef.current) return;
        
        const rect = viewModalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        viewModalRef.current.style.left = `${Math.max(0, left)}px`;
        viewModalRef.current.style.top = `${Math.max(0, top)}px`;
        viewModalRef.current.style.transform = 'none';
        
        setViewModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const centerEditModal = useCallback(() => {
        if (!editModalRef.current) return;
        
        const rect = editModalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        editModalRef.current.style.left = `${Math.max(0, left)}px`;
        editModalRef.current.style.top = `${Math.max(0, top)}px`;
        editModalRef.current.style.transform = 'none';
        
        setEditModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode for view modal
    const setViewWidthMode = useCallback((mode) => {
        if (!viewModalRef.current) return;
        
        setCurrentViewWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            viewModalRef.current.style.width = '98vw';
            viewModalRef.current.style.height = '85vh';
            viewModalRef.current.style.maxWidth = '98vw';
            viewModalRef.current.style.maxHeight = '85vh';
        } else {
            viewModalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            viewModalRef.current.style.height = 'auto';
            viewModalRef.current.style.maxWidth = '95vw';
            viewModalRef.current.style.maxHeight = '80vh';
        }
        
        setViewModalSize({ width: viewModalRef.current.offsetWidth, height: viewModalRef.current.offsetHeight });
        
        setTimeout(() => centerViewModal(), 10);
    }, [widthModes, centerViewModal]);

    // Set width mode for edit modal
    const setEditWidthMode = useCallback((mode) => {
        if (!editModalRef.current) return;
        
        setCurrentEditWidthMode(mode);
        const widthValue = editWidthModes[mode].width;
        
        if (mode === 'fullscreen') {
            editModalRef.current.style.width = '98vw';
            editModalRef.current.style.height = '90vh';
            editModalRef.current.style.maxWidth = '98vw';
            editModalRef.current.style.maxHeight = '90vh';
        } else {
            editModalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            editModalRef.current.style.height = 'auto';
            editModalRef.current.style.maxWidth = '95vw';
            editModalRef.current.style.maxHeight = '85vh';
        }
        
        setEditModalSize({ width: editModalRef.current.offsetWidth, height: editModalRef.current.offsetHeight });
        
        setTimeout(() => centerEditModal(), 10);
    }, [editWidthModes, centerEditModal]);

    // Start dragging view modal
    const startDragView = useCallback((e) => {
        if (!viewModalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern, .action-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDraggingView(true);
        
        const rect = viewModalRef.current.getBoundingClientRect();
        dragStartView.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        viewModalRef.current.style.transition = 'none';
        viewModalRef.current.classList.add('dragging');
    }, []);

    // Start dragging edit modal
    const startDragEdit = useCallback((e) => {
        if (!editModalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern, .action-btn-modern, .btn-add-product, .btn-remove-product')) {
            return;
        }
        
        e.preventDefault();
        setIsDraggingEdit(true);
        
        const rect = editModalRef.current.getBoundingClientRect();
        dragStartEdit.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        editModalRef.current.style.transition = 'none';
        editModalRef.current.classList.add('dragging');
    }, []);

    // Start resizing view modal
    const startResizeView = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!viewModalRef.current) return;
        
        setIsResizingView(true);
        resizeTypeView.current = type;
        
        const rect = viewModalRef.current.getBoundingClientRect();
        
        resizeStartView.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 350,
            minHeight: 400,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        viewModalRef.current.style.transition = 'none';
        viewModalRef.current.classList.add('resizing');
    }, []);

    // Start resizing edit modal
    const startResizeEdit = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!editModalRef.current) return;
        
        setIsResizingEdit(true);
        resizeTypeEdit.current = type;
        
        const rect = editModalRef.current.getBoundingClientRect();
        
        resizeStartEdit.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 400,
            minHeight: 500,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        editModalRef.current.style.transition = 'none';
        editModalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            // View modal dragging/resizing
            if (isDraggingView && viewModalRef.current) {
                let newLeft = e.clientX - dragStartView.current.x;
                let newTop = e.clientY - dragStartView.current.y;
                
                const rect = viewModalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                viewModalRef.current.style.left = `${newLeft}px`;
                viewModalRef.current.style.top = `${newTop}px`;
                viewModalRef.current.style.transform = 'none';
                
                setViewModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizingView && viewModalRef.current && resizeTypeView.current) {
                let newWidth = resizeStartView.current.width;
                let newHeight = resizeStartView.current.height;
                let newLeft = resizeStartView.current.left;
                let newTop = resizeStartView.current.top;
                
                const deltaX = e.clientX - resizeStartView.current.x;
                const deltaY = e.clientY - resizeStartView.current.y;
                
                switch (resizeTypeView.current) {
                    case 'e':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width + deltaX)
                        );
                        break;
                    case 'w':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width - deltaX)
                        );
                        newLeft = resizeStartView.current.left + (resizeStartView.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height + deltaY)
                        );
                        break;
                    case 'n':
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height - deltaY)
                        );
                        newTop = resizeStartView.current.top + (resizeStartView.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width + deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height - deltaY)
                        );
                        newTop = resizeStartView.current.top + (resizeStartView.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width - deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height - deltaY)
                        );
                        newLeft = resizeStartView.current.left + (resizeStartView.current.width - newWidth);
                        newTop = resizeStartView.current.top + (resizeStartView.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width + deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height + deltaY)
                        );
                        break;
                    case 'sw':
                        newWidth = Math.min(
                            resizeStartView.current.maxWidth,
                            Math.max(resizeStartView.current.minWidth, resizeStartView.current.width - deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartView.current.maxHeight,
                            Math.max(resizeStartView.current.minHeight, resizeStartView.current.height + deltaY)
                        );
                        newLeft = resizeStartView.current.left + (resizeStartView.current.width - newWidth);
                        break;
                }
                
                viewModalRef.current.style.width = `${newWidth}px`;
                viewModalRef.current.style.height = `${newHeight}px`;
                viewModalRef.current.style.left = `${newLeft}px`;
                viewModalRef.current.style.top = `${newTop}px`;
                viewModalRef.current.style.transform = 'none';
                
                setViewModalSize({ width: newWidth, height: newHeight });
                setViewModalPosition({ x: newLeft, y: newTop });
            }
            
            // Edit modal dragging/resizing
            if (isDraggingEdit && editModalRef.current) {
                let newLeft = e.clientX - dragStartEdit.current.x;
                let newTop = e.clientY - dragStartEdit.current.y;
                
                const rect = editModalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                editModalRef.current.style.left = `${newLeft}px`;
                editModalRef.current.style.top = `${newTop}px`;
                editModalRef.current.style.transform = 'none';
                
                setEditModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizingEdit && editModalRef.current && resizeTypeEdit.current) {
                let newWidth = resizeStartEdit.current.width;
                let newHeight = resizeStartEdit.current.height;
                let newLeft = resizeStartEdit.current.left;
                let newTop = resizeStartEdit.current.top;
                
                const deltaX = e.clientX - resizeStartEdit.current.x;
                const deltaY = e.clientY - resizeStartEdit.current.y;
                
                switch (resizeTypeEdit.current) {
                    case 'e':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width + deltaX)
                        );
                        break;
                    case 'w':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width - deltaX)
                        );
                        newLeft = resizeStartEdit.current.left + (resizeStartEdit.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height + deltaY)
                        );
                        break;
                    case 'n':
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height - deltaY)
                        );
                        newTop = resizeStartEdit.current.top + (resizeStartEdit.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width + deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height - deltaY)
                        );
                        newTop = resizeStartEdit.current.top + (resizeStartEdit.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width - deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height - deltaY)
                        );
                        newLeft = resizeStartEdit.current.left + (resizeStartEdit.current.width - newWidth);
                        newTop = resizeStartEdit.current.top + (resizeStartEdit.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width + deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height + deltaY)
                        );
                        break;
                    case 'sw':
                        newWidth = Math.min(
                            resizeStartEdit.current.maxWidth,
                            Math.max(resizeStartEdit.current.minWidth, resizeStartEdit.current.width - deltaX)
                        );
                        newHeight = Math.min(
                            resizeStartEdit.current.maxHeight,
                            Math.max(resizeStartEdit.current.minHeight, resizeStartEdit.current.height + deltaY)
                        );
                        newLeft = resizeStartEdit.current.left + (resizeStartEdit.current.width - newWidth);
                        break;
                }
                
                editModalRef.current.style.width = `${newWidth}px`;
                editModalRef.current.style.height = `${newHeight}px`;
                editModalRef.current.style.left = `${newLeft}px`;
                editModalRef.current.style.top = `${newTop}px`;
                editModalRef.current.style.transform = 'none';
                
                setEditModalSize({ width: newWidth, height: newHeight });
                setEditModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDraggingView || isResizingView) {
                setIsDraggingView(false);
                setIsResizingView(false);
                resizeTypeView.current = null;
                
                if (viewModalRef.current) {
                    viewModalRef.current.style.transition = '';
                    viewModalRef.current.classList.remove('dragging', 'resizing');
                }
            }
            
            if (isDraggingEdit || isResizingEdit) {
                setIsDraggingEdit(false);
                setIsResizingEdit(false);
                resizeTypeEdit.current = null;
                
                if (editModalRef.current) {
                    editModalRef.current.style.transition = '';
                    editModalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDraggingView || isResizingView || isDraggingEdit || isResizingEdit) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDraggingView, isResizingView, isDraggingEdit, isResizingEdit]);

    // Initialize modals when opened
    useEffect(() => {
        if (showViewModal && viewModalRef.current && !isViewModalInitialized) {
            setTimeout(() => {
                centerViewModal();
                setViewWidthMode('medium');
                setIsViewModalInitialized(true);
            }, 50);
        }
        
        if (!showViewModal) {
            setIsViewModalInitialized(false);
        }
    }, [showViewModal, centerViewModal, setViewWidthMode, isViewModalInitialized]);

    useEffect(() => {
        if (showEditModal && editModalRef.current && !isEditModalInitialized) {
            setTimeout(() => {
                centerEditModal();
                setEditWidthMode('medium');
                setIsEditModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsEditModalInitialized(false);
        }
    }, [showEditModal, centerEditModal, setEditWidthMode, isEditModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (viewModalRef.current && showViewModal) {
                const rect = viewModalRef.current.getBoundingClientRect();
                let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
                let newTop = Math.min(rect.top, window.innerHeight - rect.height);
                newLeft = Math.max(0, newLeft);
                newTop = Math.max(0, newTop);
                
                if (newLeft !== rect.left || newTop !== rect.top) {
                    viewModalRef.current.style.left = `${newLeft}px`;
                    viewModalRef.current.style.top = `${newTop}px`;
                    setViewModalPosition({ x: newLeft, y: newTop });
                }
            }
            
            if (editModalRef.current && showEditModal) {
                const rect = editModalRef.current.getBoundingClientRect();
                let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
                let newTop = Math.min(rect.top, window.innerHeight - rect.height);
                newLeft = Math.max(0, newLeft);
                newTop = Math.max(0, newTop);
                
                if (newLeft !== rect.left || newTop !== rect.top) {
                    editModalRef.current.style.left = `${newLeft}px`;
                    editModalRef.current.style.top = `${newTop}px`;
                    setEditModalPosition({ x: newLeft, y: newTop });
                }
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showViewModal, showEditModal]);

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    // Get payment status function
    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;
        
        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    // FIXED PRINT FUNCTION with proper product display and business info
  const handlePrint = (sale) => {
    if (!sale) return;

    // =========================
    // BUSINESS INFO
    // =========================

    const shopName =
        businessInfo?.businessname ||
        currentShop?.shop_name ||
        'Shop Name';

    const shopAddress =
        businessInfo?.businessaddress || '';

    const shopPhone =
        businessInfo?.phonenumber || '';

    const currencySymbol =
        sale.currency_symbol || 'Rs';

    // =========================
    // FORMATTERS
    // =========================

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }

        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // =========================
    // PROCESS ITEMS
    // =========================

    let calculatedSubtotal = 0;

    const itemsList = sale.items || [];

    const processedItems = itemsList.map(item => {

        const quantity =
            parseFloat(item.quantity) || 0;

        const price =
            parseFloat(
                item.price_per_unit ??
                item.price ??
                0
            );

        const total =
            parseFloat(item.total) ||
            (quantity * price);

        calculatedSubtotal += total;

        return {
            ...item,

            product_name:
                item.product_name || 'Product',

            quantity,

            price,

            total,

            unit:
                item.unit || '',

            rolls:
                item.rolls || 0,

            color:
                item.product_colorcode ||
                item.color ||
                '',

            size:
                item.size || ''
        };
    });

    // =========================
    // TOTALS
    // =========================

    const subtotal =
        sale.subtotal ||
        calculatedSubtotal.toFixed(2);

    const tax =
        sale.tax || '0.00';

    const discount =
        sale.discount || '0.00';

    const grandTotal =
        sale.grand_total ||
        (
            parseFloat(subtotal) +
            parseFloat(tax) -
            parseFloat(discount)
        ).toFixed(2);

    const paidAmount =
        sale.paid_amount || grandTotal;

    const balance =
        sale.balance ||
        (
            parseFloat(grandTotal) -
            parseFloat(paidAmount)
        ).toFixed(2);

    const paymentStatus =
        getPaymentStatus(
            paidAmount,
            grandTotal
        );

    // =========================
    // PRINT WINDOW
    // =========================

    const printWindow =
        window.open('', '_blank');

    if (printWindow) {

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>

                <title>
                    Invoice - ${sale.bill_number || 'Invoice'}
                </title>

                <meta charset="UTF-8" />

                <style>

                    *{
                        margin:0;
                        padding:0;
                        box-sizing:border-box;
                    }

                    @page{
                        size:A5;
                        margin:0;
                    }

                    body{
                        font-family:
                            'Inter',
                            -apple-system,
                            BlinkMacSystemFont,
                            'Segoe UI',
                            sans-serif;

                        background:#ffffff;
                        color:#111827;

                        line-height:1.3;
                    }

                    /* =========================
                       INVOICE
                    ========================= */

                    .invoice{
                        width:100%;
                        max-width:148mm;

                        margin:0 auto;

                        padding:3.5mm 4mm;

                        background:white;
                    }

                    /* =========================
                       HEADER
                    ========================= */

                    .invoice-header{
                        text-align:center;

                        padding-bottom:2.5mm;

                        border-bottom:
                            1px solid #dbe2ea;
                    }

                    .shop-name{
                        font-size:18pt;
                        font-weight:800;

                        color:#111827;

                        margin-bottom:0.8mm;

                        letter-spacing:-0.3px;
                    }

                    .shop-address{
                        font-size:7pt;
                        color:#6b7280;

                        margin-bottom:0.5mm;
                    }

                    .shop-contact{
                        font-size:7pt;
                        color:#374151;

                        font-weight:600;
                    }

                    .invoice-title{
                        margin-top:2mm;

                        text-align:center;

                        font-size:10pt;
                        font-weight:800;

                        letter-spacing:1px;

                        color:#111827;
                    }

                    /* =========================
                       INFO GRID
                    ========================= */

                    .info-grid{
                        display:grid;

                        grid-template-columns:
                            repeat(2,1fr);

                        gap:1.2mm 4mm;

                        padding:2mm 0;

                        margin-top:2mm;

                        border-bottom:
                            1px dashed #d1d5db;
                    }

                    .info-item{
                        display:flex;

                        justify-content:space-between;

                        align-items:center;
                    }

                    .info-label{
                        font-size:7pt;

                        font-weight:700;

                        color:#6b7280;
                    }

                    .info-value{
                        font-size:7.2pt;

                        font-weight:600;

                        color:#111827;

                        text-align:right;
                    }

                    /* =========================
                       CUSTOMER
                    ========================= */

                    .customer-section{
                        margin-top:2mm;

                        padding-bottom:2mm;

                        border-bottom:
                            1px dashed #d1d5db;
                    }

                    /* =========================
                       PRODUCTS TABLE
                    ========================= */

                    .items-table{
                        width:100%;

                        margin-top:3mm;

                        border-collapse:collapse;

                        font-size:7.2pt;
                    }

                    .items-table thead th{
                        background:#f3f4f6;

                        color:#111827;

                        font-weight:800;

                        padding:2mm 1.5mm;

                        border:
                            1px solid #d1d5db;

                        text-transform:uppercase;

                        font-size:6.8pt;
                    }

                    .items-table tbody td{
                        padding:2mm 1.5mm;

                        border:
                            1px solid #e5e7eb;

                        vertical-align:middle;
                    }

                    .items-table tbody tr:nth-child(even){
                        background:#fafafa;
                    }

                    .product-name{
                        font-size:7.2pt;

                        font-weight:700;

                        color:#111827;

                        line-height:1.3;
                    }

                    .product-sub{
                        margin-top:0.5mm;

                        font-size:6pt;

                        color:#6b7280;
                    }

                    .text-right{
                        text-align:right;
                    }

                    .text-center{
                        text-align:center;
                    }

                    /* =========================
                       TOTALS
                    ========================= */

                    .totals-section{
                        width:48%;

                        margin-left:auto;

                        margin-top:3mm;
                    }

                    .total-row{
                        display:flex;

                        justify-content:space-between;

                        align-items:center;

                        padding:1.2mm 0;

                        font-size:7.5pt;
                    }

                    .total-row .label{
                        font-weight:700;

                        color:#4b5563;
                    }

                    .total-row .value{
                        font-weight:700;

                        color:#111827;
                    }

                    .grand-total-row{
                        margin-top:1mm;

                        padding-top:1.5mm;

                        border-top:
                            1.5px solid #111827;
                    }

                    .grand-total-row .label,
                    .grand-total-row .value{
                        font-size:9pt;

                        font-weight:800;

                        color:#111827;
                    }

                    /* =========================
                       PAYMENT
                    ========================= */

                    .payment-info{
                        margin-top:3mm;

                        display:flex;

                        justify-content:space-between;

                        align-items:center;

                        padding-top:2mm;

                        border-top:
                            1px dashed #d1d5db;

                        font-size:7pt;

                        font-weight:600;
                    }

                    /* =========================
                       NOTES
                    ========================= */

                    .notes-box{
                        margin-top:2mm;

                        padding-top:1.5mm;

                        border-top:
                            1px dashed #d1d5db;

                        font-size:6.8pt;

                        color:#374151;
                    }

                    /* =========================
                       FOOTER
                    ========================= */

                    .footer{
                        margin-top:4mm;

                        padding-top:2mm;

                        border-top:
                            1px dashed #d1d5db;

                        text-align:center;
                    }

                    .thankyou{
                        font-size:8pt;

                        font-weight:800;

                        color:#111827;
                    }

                    .footer-text{
                        font-size:6.5pt;

                        color:#6b7280;

                        margin-top:0.5mm;
                    }

                    .support-numbers{
                        font-size:6.5pt;

                        color:#6b7280;

                        margin-top:0.5mm;
                    }

                    /* =========================
                       PRINT
                    ========================= */

                    @media print{

                        body{
                            -webkit-print-color-adjust:exact;
                            print-color-adjust:exact;
                        }

                        .invoice{
                            padding:3mm;
                        }

                        table tr{
                            break-inside:avoid;
                        }
                    }

                </style>

            </head>

            <body>

                <div class="invoice">

                    <!-- HEADER -->

                    <div class="invoice-header">

                        <div class="shop-name">
                            ${shopName}
                        </div>

                        ${
                            shopAddress
                            ? `
                                <div class="shop-address">
                                    ${shopAddress}
                                </div>
                            `
                            : ''
                        }

                        ${
                            shopPhone
                            ? `
                                <div class="shop-contact">
                                    📞 ${shopPhone}
                                </div>
                            `
                            : ''
                        }

                        <div class="invoice-title">
                            ${translations.invoice || 'TAX INVOICE'}
                        </div>

                    </div>

                    <!-- INFO -->

                    <div class="info-grid">

                        <div class="info-item">
                            <span class="info-label">
                                ${translations.billNumber || 'Bill No'}
                            </span>

                            <span class="info-value">
                                #${sale.bill_number}
                            </span>
                        </div>

                        <div class="info-item">
                            <span class="info-label">
                                ${translations.date || 'Date'}
                            </span>

                            <span class="info-value">
                                ${formatDate(
                                    sale.sale_date || sale.date
                                )}
                            </span>
                        </div>

                        <div class="info-item">
                            <span class="info-label">
                                ${translations.time || 'Time'}
                            </span>

                            <span class="info-value">
                                ${formatTime()}
                            </span>
                        </div>

                        <div class="info-item">
                            <span class="info-label">
                                ${translations.cashier || 'Cashier'}
                            </span>

                            <span class="info-value">
                                ${
                                    sale.username ||
                                    user?.username ||
                                    'Staff'
                                }
                            </span>
                        </div>

                    </div>

                    <!-- CUSTOMER -->

                    ${
                        (
                            sale.customer_name ||
                            sale.customer_phone ||
                            sale.customer_address
                        )
                        ? `
                            <div class="customer-section">

                                <div class="info-grid"
                                     style="
                                        margin-top:0;
                                        padding:0;
                                        border:none;
                                     ">

                                    ${
                                        sale.customer_name
                                        ? `
                                            <div class="info-item">

                                                <span class="info-label">
                                                    ${translations.customerName || 'Customer'}
                                                </span>

                                                <span class="info-value">
                                                    ${sale.customer_name}
                                                </span>

                                            </div>
                                        `
                                        : ''
                                    }

                                    ${
                                        sale.customer_phone
                                        ? `
                                            <div class="info-item">

                                                <span class="info-label">
                                                    ${translations.phone || 'Phone'}
                                                </span>

                                                <span class="info-value">
                                                    ${sale.customer_phone}
                                                </span>

                                            </div>
                                        `
                                        : ''
                                    }

                                    ${
                                        sale.customer_address
                                        ? `
                                            <div class="info-item"
                                                 style="grid-column:1/-1;">

                                                <span class="info-label">
                                                    ${translations.customerAddress || 'Address'}
                                                </span>

                                                <span class="info-value">
                                                    ${sale.customer_address}
                                                </span>

                                            </div>
                                        `
                                        : ''
                                    }

                                </div>

                            </div>
                        `
                        : ''
                    }

                    <!-- PRODUCTS -->

                    <table class="items-table">

                        <thead>

                            <tr>

                                <th style="width:34%">
                                    ${translations.productName || 'Product'}
                                </th>

                               

                                <th style="width:12%"
                                    class="text-center">

                                    ${translations.unit || 'Unit'}
                                </th>

 <th style="width:10%"
                                    class="text-center">

                                    ${translations.quantity || 'Qty'}
                                </th>

                                <th style="width:10%"
                                    class="text-center">

                                    ${translations.rolls || 'Rolls'}
                                </th>

                                <th style="width:16%"
                                    class="text-right">

                                    ${translations.price || 'Price'}
                                </th>

                                <th style="width:18%"
                                    class="text-right">

                                    ${translations.total || 'Total'}
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            ${
                                processedItems.length > 0

                                ? processedItems.map(item => `

                                    <tr>

                                        <td>

                                            <div class="product-name">

                                                ${item.product_name}

                                                ${
                                                    item.color
                                                    ? ` - ${item.color}`
                                                    : ''
                                                }

                                            </div>

                                            ${
                                                item.size
                                                ? `
                                                    <div class="product-sub">
                                                        Size: ${item.size}
                                                    </div>
                                                `
                                                : ''
                                            }

                                        </td>

                                        <td class="text-center">
                                            ${item.unit || '-'}
                                        </td>

                                          <td class="text-center">
                                            <strong>
                                                ${item.quantity.toFixed(2)}
                                            </strong>
                                        </td>

                                        <td class="text-center">
                                            ${item.rolls || 0}
                                        </td>

                                        <td class="text-right">
                                            ${currencySymbol}
                                            ${item.price.toFixed(2)}
                                        </td>

                                        <td class="text-right">
                                            <strong>
                                                ${currencySymbol}
                                                ${item.total.toFixed(2)}
                                            </strong>
                                        </td>

                                    </tr>

                                `).join('')

                                : `
                                    <tr>

                                        <td colspan="6"
                                            class="text-center"
                                            style="
                                                padding:5mm;
                                                color:#ef4444;
                                            ">

                                            No products found

                                        </td>

                                    </tr>
                                `
                            }

                        </tbody>

                    </table>

                    <!-- TOTALS -->

                    <div class="totals-section">

                        <div class="total-row">

                            <span class="label">
                                ${translations.subtotal || 'Subtotal'}
                            </span>

                            <span class="value">
                                ${currencySymbol}
                                ${parseFloat(subtotal).toFixed(2)}
                            </span>

                        </div>

                        ${
                            parseFloat(tax) > 0
                            ? `
                                <div class="total-row">

                                    <span class="label">
                                        ${translations.tax || 'Tax'}
                                    </span>

                                    <span class="value">
                                        ${currencySymbol}
                                        ${parseFloat(tax).toFixed(2)}
                                    </span>

                                </div>
                            `
                            : ''
                        }

                        ${
                            parseFloat(discount) > 0
                            ? `
                                <div class="total-row">

                                    <span class="label">
                                        ${translations.discount || 'Discount'}
                                    </span>

                                    <span class="value"
                                          style="color:#059669;">

                                        -${currencySymbol}
                                        ${parseFloat(discount).toFixed(2)}

                                    </span>

                                </div>
                            `
                            : ''
                        }

                        <div class="total-row grand-total-row">

                            <span class="label">
                                ${translations.grandTotal || 'Grand Total'}
                            </span>

                            <span class="value">
                                ${currencySymbol}
                                ${parseFloat(grandTotal).toFixed(2)}
                            </span>

                        </div>

                        <div class="total-row">

                            <span class="label">
                                ${translations.paidAmount || 'Paid'}
                            </span>

                            <span class="value">

                                ${currencySymbol}
                                ${parseFloat(paidAmount).toFixed(2)}

                            </span>

                        </div>

                        <div class="total-row">

                            <span class="label">
                                ${translations.balance || 'Balance'}
                            </span>

                            <span class="value">

                                ${currencySymbol}
                                ${parseFloat(balance).toFixed(2)}

                            </span>

                        </div>

                    </div>

                    <!-- PAYMENT -->

                    <div class="payment-info">

                        <span>
                            ${translations.paymentMethod || 'Payment'}:
                            <strong>
                                ${(sale.payment_method || 'CASH').toUpperCase()}
                            </strong>
                        </span>

                        <span>
                            ${translations.paymentStatus || 'Status'}:
                            <strong>
                                ${paymentStatus.text.toUpperCase()}
                            </strong>
                        </span>

                    </div>

                    <!-- NOTES -->

                    ${
                        sale.notes
                        ? `
                            <div class="notes-box">
                                Note: ${sale.notes}
                            </div>
                        `
                        : ''
                    }

                    <!-- FOOTER -->

                    <div class="footer">

                        <div class="thankyou">
                            ${translations.thankYou || 'Thank you for your business!'}
                        </div>

                    

                    </div>

                </div>

            </body>

            </html>
        `);

        printWindow.document.close();

        printWindow.focus();

        setTimeout(() => {

            printWindow.print();

            printWindow.onafterprint = () => {
                printWindow.close();
            };

        }, 300);
    }
};

    // Product editing functions
    const handleAddEditItem = () => {
        setEditFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                id: Date.now(),
                product_name: '',
                productName: '',
                quantity: 1,
                price: 0,
                total: 0,
                batch_no: '',
                unit: '',
                rolls: 0,
                color: '',
                size: ''
            }]
        }));
    };

    const handleRemoveEditItem = (index) => {
        const newItems = [...editFormData.items];
        newItems.splice(index, 1);
        setEditFormData(prev => ({
            ...prev,
            items: newItems
        }));
        recalculateTotals(newItems);
    };

    const handleEditItemChange = (index, field, value) => {
        const newItems = [...editFormData.items];
        newItems[index][field] = value;
        
        // Recalculate total for this item
        if (field === 'quantity' || field === 'price') {
            const quantity = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].price) || 0;
            newItems[index].total = quantity * price;
        }
        
        setEditFormData(prev => ({
            ...prev,
            items: newItems
        }));
        
        recalculateTotals(newItems);
    };

    const recalculateTotals = (items) => {
        const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        const tax = parseFloat(editFormData.tax) || 0;
        const discount = parseFloat(editFormData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paidAmount = parseFloat(editFormData.paid_amount) || 0;
        const balance = grandTotal - paidAmount;
        
        setEditFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2)
        }));
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                document.documentElement.style.setProperty('--customer-primary-color', color);
                document.documentElement.style.setProperty('--customer-primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--customer-primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            setCurrentTheme(theme);
            applyTheme(theme);
        };

        window.addEventListener('storage', handleThemeChange);
        return () => window.removeEventListener('storage', handleThemeChange);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await sellListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        document.documentElement.style.direction = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    // Fetch sales when shop changes
    useEffect(() => {
        if (currentShop) {
            fetchSales();
            fetchAvailableShops();
            fetchBusinessInfo();
        } else {
            setLoading(false);
        }
    }, [currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [sales, searchTerm, filterType, filterBy, billNoFilter, customerFilter, phoneFilter, addedByFilter, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    {translations.shopSales || 'Showing sales from this shop'}
                </div>
                <button
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    const fetchSales = async () => {
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSales(data.data || []);

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No sales found in your shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...sales];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.bill_number?.toLowerCase().includes(term) ||
                sale.customer_name?.toLowerCase().includes(term) ||
                sale.customer_phone?.toLowerCase().includes(term) ||
                sale.username?.toLowerCase().includes(term)
            );
        }

        if (filterType === 'paid') {
            filtered = filtered.filter(sale => {
                const balance = parseFloat(sale.balance) || 0;
                return balance === 0;
            });
        } else if (filterType === 'unpaid') {
            filtered = filtered.filter(sale => {
                const paid = parseFloat(sale.paid_amount) || 0;
                return paid === 0;
            });
        } else if (filterType === 'partiallyPaid') {
            filtered = filtered.filter(sale => {
                const paid = parseFloat(sale.paid_amount) || 0;
                const grandTotal = parseFloat(sale.grand_total) || 0;
                const balance = parseFloat(sale.balance) || 0;
                return paid > 0 && balance > 0;
            });
        }

        if (filterBy === 'billNo' && billNoFilter.trim()) {
            const billNoTerm = billNoFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.bill_number?.toLowerCase().includes(billNoTerm)
            );
        }

        if (filterBy === 'customer' && customerFilter.trim()) {
            const customerTerm = customerFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.customer_name?.toLowerCase().includes(customerTerm)
            );
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            const phoneTerm = phoneFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.customer_phone?.toLowerCase().includes(phoneTerm)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const addedByTerm = addedByFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.username?.toLowerCase().includes(addedByTerm)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                saleDate.setHours(0, 0, 0, 0);
                return saleDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                return saleDate <= end;
            });
        }

        setFilteredSales(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setBillNoFilter('');
        setCustomerFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleView = async (sale) => {
        setSelectedSale(sale);
        
        if (!sale.items && sale.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: sale.id,
                        shop_id: currentShop.shop_id
                    })
                });

                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedSale(data.data);
                }
            } catch (error) {
                console.error('Error fetching sale details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handleEdit = (sale) => {
        setSelectedSale(sale);
        
        const formattedDate = sale.date ? sale.date.split('T')[0] : '';
        
        // Ensure items are properly formatted
        const items = (sale.items || []).map(item => ({
            id: item.id || Date.now(),
            product_name: item.product_name || item.productName || '',
            productName: item.product_name || item.productName || '',
            quantity: parseFloat(item.quantity) || 0,
            price: parseFloat(item.price) || 0,
            total: parseFloat(item.total) || (parseFloat(item.quantity) * parseFloat(item.price)) || 0,
            batch_no: item.batch_no || '',
            unit: item.unit || '',
            rolls: item.rolls || 0,
            color: item.color || '',
            size: item.size || ''
        }));
        
        setEditFormData({
            id: sale.id,
            date: formattedDate,
            bill_number: sale.bill_number || '',
            customer_name: sale.customer_name || '',
            customer_phone: sale.customer_phone || '',
            customer_address: sale.customer_address || '',
            subtotal: sale.subtotal || '',
            tax: sale.tax || '0',
            discount: sale.discount || '0',
            grand_total: sale.grand_total || '',
            paid_amount: sale.paid_amount || '',
            balance: sale.balance || '',
            payment_method: sale.payment_method || 'cash',
            payment_status: sale.payment_status || 'unpaid',
            notes: sale.notes || '',
            currency_symbol: sale.currency_symbol || 'Rs',
            currency_code: sale.currency_code || 'PKR',
            items: items
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (saleId) => {
        setSaleToDelete(saleId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!saleToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: saleToDelete,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Sale deleted successfully');
                fetchSales();
                setShowDeleteModal(false);
                setSaleToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting sale');
            }
        } catch (error) {
            console.error('Error deleting sale:', error);
            showToast('error', translations.deleteError || 'Error deleting sale');
        }
    };

    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.date) {
            newErrors.date = translations.dateRequired || 'Date is required';
        }

        if (!editFormData.bill_number.trim()) {
            newErrors.bill_number = translations.billNoRequired || 'Bill number is required';
        }

        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            newErrors.grand_total = translations.grandTotalRequired || 'Grand total is required';
        }

        if (editFormData.paid_amount === '' || editFormData.paid_amount === null) {
            newErrors.paid_amount = translations.paidRequired || 'Paid amount is required';
        }

        if (editFormData.paid_amount && editFormData.grand_total && parseFloat(editFormData.paid_amount) > parseFloat(editFormData.grand_total)) {
            newErrors.paid_amount = 'Paid amount cannot exceed grand total';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        
        let updatedFormData = { ...editFormData, [name]: value };

        if (name === 'grand_total' || name === 'paid_amount' || name === 'tax' || name === 'discount') {
            const grandTotal = parseFloat(name === 'grand_total' ? value : editFormData.grand_total) || 0;
            const paid = parseFloat(name === 'paid_amount' ? value : editFormData.paid_amount) || 0;
            const tax = parseFloat(name === 'tax' ? value : editFormData.tax) || 0;
            const discount = parseFloat(name === 'discount' ? value : editFormData.discount) || 0;
            const subtotal = parseFloat(editFormData.subtotal) || 0;
            const newGrandTotal = subtotal + tax - discount;
            
            updatedFormData.grand_total = newGrandTotal.toFixed(2);
            updatedFormData.balance = (newGrandTotal - paid).toFixed(2);
            
            if (newGrandTotal > 0) {
                if (paid >= newGrandTotal) {
                    updatedFormData.payment_status = 'paid';
                } else if (paid === 0) {
                    updatedFormData.payment_status = 'unpaid';
                } else {
                    updatedFormData.payment_status = 'partial';
                }
            }
        }

        setEditFormData(updatedFormData);

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSale = async (e) => {
        e.preventDefault();

        if (!selectedSale || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            // Prepare items data
            const itemsData = editFormData.items.map(item => ({
                product_name: item.product_name || item.productName,
                quantity: item.quantity,
                price: item.price,
                total: item.total,
                batch_no: item.batch_no || '',
                unit: item.unit || '',
                rolls: item.rolls || 0,
                color: item.color || '',
                size: item.size || ''
            }));

            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedSale.id,
                    date: editFormData.date,
                    bill_number: editFormData.bill_number,
                    customer_name: editFormData.customer_name,
                    customer_phone: editFormData.customer_phone,
                    customer_address: editFormData.customer_address,
                    subtotal: editFormData.subtotal,
                    tax: editFormData.tax,
                    discount: editFormData.discount,
                    grand_total: editFormData.grand_total,
                    paid_amount: editFormData.paid_amount,
                    balance: editFormData.balance,
                    payment_method: editFormData.payment_method,
                    payment_status: editFormData.payment_status,
                    notes: editFormData.notes,
                    items: itemsData,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.saleUpdated || 'Sale updated successfully');
                fetchSales();
                setShowEditModal(false);
                setSelectedSale(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating sale');
            }
        } catch (error) {
            console.error('Error updating sale:', error);
            showToast('error', translations.updateError || 'Error updating sale');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const getPaymentIcon = (method) => {
        switch (method) {
            case 'cash':
                return faMoneyBillWave;
            case 'card':
                return faCreditCard;
            case 'upi':
                return faWallet;
            case 'bank_transfer':
                return faUniversity;
            default:
                return faDollarSign;
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faShoppingCart} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.salesList || 'Sales List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : translations.shopSales || 'Shop Sales'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.filterBy || 'Filter By'}</option>
                            <option value="billNo">{translations.billNoFilter || 'Bill Number'}</option>
                            <option value="customer">{translations.customerFilter || 'Customer Name'}</option>
                            <option value="phone">{translations.phone || 'Phone'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                        </select>
                    </div>

                    {filterBy === 'billNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={billNoFilter}
                                onChange={(e) => setBillNoFilter(e.target.value)}
                                placeholder={translations.billNoFilter || 'Bill number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'customer' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                placeholder={translations.customerFilter || 'Customer name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phone || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                    dir="ltr"
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                    dir="ltr"
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.paymentStatus || 'Payment Status'}</option>
                            <option value="paid">{translations.paidFilter || 'Paid'}</option>
                            <option value="unpaid">{translations.unpaidFilter || 'Unpaid'}</option>
                            <option value="partiallyPaid">{translations.partiallyPaidFilter || 'Partially Paid'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search sales in shop...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchSales}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop sales data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view sales'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="table-scroll-container" dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.date || 'Date'}</th>
                                            <th className="centered-cell">{translations.billNumber || 'Bill No'}</th>
                                            <th className="centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="centered-cell">{translations.items || 'Items'}</th>
                                            <th className="centered-cell">{translations.rolls || 'Rolls'}</th>
                                            <th className="centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="centered-cell">{translations.paymentStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSales.map((sale, index) => {
                                            const paymentStatus = getPaymentStatus(sale.paid_amount, sale.grand_total);
                                            const itemCount = sale.items ? sale.items.length : (sale.item_count || '—');
                                            // Calculate total rolls from items
                                            const totalRolls = sale.items ? sale.items.reduce((sum, item) => sum + (parseInt(item.rolls) || 0), 0) : 0;
                                            const addedBy = sale.username || '-';
                                            const currencySymbol = getCurrencySymbol(sale);
                                            
                                            return (
                                                <tr key={sale.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                                                                                                                            </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                            <span className="cell-text">{formatDateGlobal(sale.date)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faReceipt} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{sale.bill_number}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{sale.customer_name || (translations.walkInCustomer || 'Walk-in')}</span>
                                                            {sale.customer_phone && (
                                                                <div className="cell-subtext">
                                                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                                                    <span>{sale.customer_phone}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="items-badge">
                                                                <FontAwesomeIcon icon={faBox} />
                                                                <span>{itemCount}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="rolls-badge">
                                                                <FontAwesomeIcon icon={faLayerGroup} />
                                                                <span>{totalRolls}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="grand-total" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                                {formatCurrency(sale.grand_total, currencySymbol)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="paid-amount" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                                                {formatCurrency(sale.paid_amount, currencySymbol)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`balance-amount ${parseFloat(sale.balance) > 0 ? 'has-balance' : 'no-balance'}`} 
                                                                  style={{ 
                                                                      color: parseFloat(sale.balance) > 0 ? '#d97706' : '#059669',
                                                                      fontWeight: 'bold'
                                                                  }}>
                                                                {formatCurrency(sale.balance, currencySymbol)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div 
                                                                className="payment-status-badge"
                                                                style={{ 
                                                                    backgroundColor: paymentStatus.color + '20',
                                                                    color: paymentStatus.color,
                                                                    border: `1px solid ${paymentStatus.color}40`
                                                                }}
                                                            >
                                                                {paymentStatus.text}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div className="payment-method">
                                                                <FontAwesomeIcon icon={getPaymentIcon(sale.payment_method)} />
                                                                <span>{translations.paymentMethods?.[sale.payment_method] || sale.payment_method}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`added-by-badge ${addedBy === user?.username ? 'self' : ''}`}>
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{addedBy}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleView(sale)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handlePrint(sale)}
                                                                className="action-btn-modern print-btn-modern"
                                                                title={translations.print || 'Print'}
                                                            >
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(sale)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(sale.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {currentSales.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <p>{translations.noUserRecords || 'No sales found in your shop'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {filteredSales.length > 0 && (
                        <div className="pagination-modern" dir={isRTL ? 'rtl' : 'ltr'}>
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredSales.length)} {translations.of || 'of'} {filteredSales.length} {translations.sales || 'sales'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                                {uniqueCurrencies > 1 && (
                                    <span style={{ marginLeft: isRTL ? '0' : '20px', marginRight: isRTL ? '20px' : '0', color: '#059669', fontWeight: '600' }}>
                                        {uniqueCurrencies} {translations.currencyBreakdown || 'Currencies'}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal - Resizable and Movable */}
            {showViewModal && selectedSale && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div 
                        ref={viewModalRef}
                        className="modal-content-modern view-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        style={{
                            position: 'fixed',
                            left: viewModalPosition.x || '50%',
                            top: viewModalPosition.y || '50%',
                            transform: viewModalPosition.x === 0 && viewModalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: viewModalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResizeView(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResizeView(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResizeView(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResizeView(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResizeView(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResizeView(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResizeView(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResizeView(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDragView}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.saleDetails || 'Sale Details'}</h3>
                                <span className="sale-number-badge">#{selectedSale.bill_number}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentViewWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setViewWidthMode(key)}
                                        title={mode.label}
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="modal-body-modern view-modal-body">
                            <div className="detail-section">
                                <h4 className="section-title">{translations.saleInfo || 'Sale Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.saleId || 'Sale ID'}:</span>
                                        <span className="detail-value">#{selectedSale.id}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.date || 'Date'}:</span>
                                        <span className="detail-value">{formatDateGlobal(selectedSale.date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.billNumber || 'Bill Number'}:</span>
                                        <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedSale.bill_number}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.customerName || 'Customer Name'}:</span>
                                        <span className="detail-value">{selectedSale.customer_name || (translations.walkInCustomer || 'Walk-in Customer')}</span>
                                    </div>
                                    {selectedSale.customer_phone && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.phone || 'Phone'}:</span>
                                            <span className="detail-value">{selectedSale.customer_phone}</span>
                                        </div>
                                    )}
                                    {selectedSale.customer_address && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.customerAddress || 'Address'}:</span>
                                            <span className="detail-value">{selectedSale.customer_address}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentMethod || 'Payment Method'}:</span>
                                        <span className="detail-value">
                                            <FontAwesomeIcon icon={getPaymentIcon(selectedSale.payment_method)} />
                                            {translations.paymentMethods?.[selectedSale.payment_method] || selectedSale.payment_method}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentStatus || 'Payment Status'}:</span>
                                        <span className="detail-value">
                                            <div className="payment-status-badge" style={{ 
                                                backgroundColor: getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color + '20',
                                                color: getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color,
                                                border: `1px solid ${getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color}40`,
                                                display: 'inline-block'
                                            }}>
                                                {getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).text}
                                            </div>
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.grandTotal || 'Grand Total'}:</span>
                                        <span className="detail-value" style={{ color: '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedSale.grand_total, selectedSale.currency_symbol)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paidAmount || 'Paid Amount'}:</span>
                                        <span className="detail-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedSale.paid_amount, selectedSale.currency_symbol)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.balance || 'Balance'}:</span>
                                        <span className="detail-value" style={{ color: parseFloat(selectedSale.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedSale.balance, selectedSale.currency_symbol)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="detail-value">
                                            <span className="added-by-badge" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                                                <FontAwesomeIcon icon={faUser} size="xs" />
                                                {selectedSale.username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    {selectedSale.notes && (
                                        <div className="detail-row-modern full-width">
                                            <span className="detail-label">{translations.notes || 'Notes'}:</span>
                                            <span className="detail-value notes-text">{selectedSale.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedSale.items && selectedSale.items.length > 0 && (
                                <div className="detail-section">
                                    <h4 className="section-title">{translations.items || 'Sale Items'}</h4>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>{translations.serialNo || '#'}</th>
                                                    <th>{translations.productName || 'Product'}</th>
                                                    <th>{translations.batchNo || 'Batch'}</th>
                                                    <th>{translations.unit || 'Unit'}</th>
                                                    <th>{translations.rolls || 'Rolls'}</th>
                                                    <th>{translations.price || 'Price'}</th>
                                                    <th>{translations.quantity || 'Qty'}</th>
                                                    <th>{translations.total || 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedSale.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="batch-cell">{item.batch_no}</td>
                                                        <td>{item.unit}</td>
                                                        <td className="amount-cell">{parseInt(item.rolls) || 0}</td>
                                                        <td className="amount-cell">{formatCurrency(item.price, selectedSale.currency_symbol)}</td>
                                                        <td className="amount-cell">{parseFloat(item.quantity).toFixed(2)}</td>
                                                        <td className="amount-cell total">{formatCurrency(item.total, selectedSale.currency_symbol)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="7" className="text-right">{translations.subtotal || 'Subtotal'}:</td>
                                                    <td className="amount-cell">{formatCurrency(selectedSale.subtotal, selectedSale.currency_symbol)}</td>
                                                </tr>
                                                {parseFloat(selectedSale.tax) > 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="text-right">{translations.tax || 'Tax'}:</td>
                                                        <td className="amount-cell">{formatCurrency(selectedSale.tax, selectedSale.currency_symbol)}</td>
                                                    </tr>
                                                )}
                                                {parseFloat(selectedSale.discount) > 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="text-right">{translations.discount || 'Discount'}:</td>
                                                        <td className="amount-cell">-{formatCurrency(selectedSale.discount, selectedSale.currency_symbol)}</td>
                                                    </tr>
                                                )}
                                                <tr className="grand-total-row">
                                                    <td colSpan="7" className="text-right">{translations.grandTotal || 'Grand Total'}:</td>
                                                    <td className="amount-cell grand-total">{formatCurrency(selectedSale.grand_total, selectedSale.currency_symbol)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="7" className="text-right">{translations.paidAmount || 'Paid'}:</td>
                                                    <td className="amount-cell paid">{formatCurrency(selectedSale.paid_amount, selectedSale.currency_symbol)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="7" className="text-right">{translations.balance || 'Balance'}:</td>
                                                    <td className="amount-cell balance">{formatCurrency(selectedSale.balance, selectedSale.currency_symbol)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                            <button
                                className="btn-modern btn-primary-modern"
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleEdit(selectedSale);
                                }}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                                {translations.edit || 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal with Product Editing - Resizable and Movable */}
            {showEditModal && selectedSale && (
                <div className="modal-overlay-modern" onClick={() => setShowEditModal(false)}>
                    <div 
                        ref={editModalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()} 
                        dir={isRTL ? 'rtl' : 'ltr'}
                        style={{
                            position: 'fixed',
                            left: editModalPosition.x || '50%',
                            top: editModalPosition.y || '50%',
                            transform: editModalPosition.x === 0 && editModalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: editModalSize.width,
                            minWidth: '400px',
                            maxWidth: '95vw',
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResizeEdit(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResizeEdit(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResizeEdit(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResizeEdit(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResizeEdit(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResizeEdit(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResizeEdit(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResizeEdit(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDragEdit} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faUserEdit} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editSale || 'Edit Sale'}</h3>
                                <span className="sale-number-badge">#{selectedSale.bill_number}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(editWidthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`width-btn ${currentEditWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setEditWidthMode(key)}
                                        title={mode.label}
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowEditModal(false)}
                                type="button"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateSale} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="edit-form-container">
                                    {/* Order Information Section */}
                                    <div className="form-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faReceipt} />Sale Information</h4>
                                        <div className="form-grid edit-form-grid">
                                            <div className={`form-group ${editFormErrors.date ? 'has-error' : ''}`}>
                                                <label htmlFor="edit_date">{translations.date || 'Date'} <span className="required">*</span></label>
                                                <input
                                                    type="date"
                                                    id="edit_date"
                                                    name="date"
                                                    value={editFormData.date}
                                                    onChange={handleEditFormChange}
                                                    required
                                                    max={getTodayDate()}
                                                    disabled={editLoading}
                                                    dir="ltr"
                                                />
                                                {editFormErrors.date && (
                                                    <div className="error-message">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        {editFormErrors.date}
                                                    </div>
                                                )}
                                            </div>

                                            <div className={`form-group ${editFormErrors.bill_number ? 'has-error' : ''}`}>
                                                <label htmlFor="edit_bill_number">{translations.billNumber || 'Bill Number'} <span className="required">*</span></label>
                                                <input
                                                    type="text"
                                                    id="edit_bill_number"
                                                    name="bill_number"
                                                    value={editFormData.bill_number}
                                                    onChange={handleEditFormChange}
                                                    required
                                                    placeholder={translations.billNumber || 'Enter bill number'}
                                                    disabled={true}
                                                    dir={isRTL ? 'rtl' : 'ltr'}
                                                    className="readonly-input"
                                                />
                                                {editFormErrors.bill_number && (
                                                    <div className="error-message">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        {editFormErrors.bill_number}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="edit_customer_name">{translations.customerName || 'Customer Name'}</label>
                                                <input
                                                    type="text"
                                                    id="edit_customer_name"
                                                    name="customer_name"
                                                    value={editFormData.customer_name}
                                                    onChange={handleEditFormChange}
                                                    placeholder={translations.customerName || 'Enter customer name'}
                                                    disabled={editLoading}
                                                    dir={isRTL ? 'rtl' : 'ltr'}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="edit_customer_phone">{translations.phone || 'Phone'}</label>
                                                <input
                                                    type="tel"
                                                    id="edit_customer_phone"
                                                    name="customer_phone"
                                                    value={editFormData.customer_phone}
                                                    onChange={handleEditFormChange}
                                                    placeholder={translations.phone || 'Enter phone number'}
                                                    disabled={editLoading}
                                                    dir="ltr"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="edit_payment_method">{translations.paymentMethod || 'Payment Method'}</label>
                                                <select
                                                    id="edit_payment_method"
                                                    name="payment_method"
                                                    value={editFormData.payment_method}
                                                    onChange={handleEditFormChange}
                                                    disabled={editLoading}
                                                    dir={isRTL ? 'rtl' : 'ltr'}
                                                >
                                                    {paymentMethods.map(method => (
                                                        <option key={method.value} value={method.value}>
                                                            {method.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products Section - Editable with ROLLS */}
                                    <div className="form-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faBox} />Order Items <span className="required">*</span></h4>
                                        <div className="items-table-container">
                                            <div className="table-header-actions">
                                                <h4><FontAwesomeIcon icon={faBox} />{translations.editItems || 'Products'}</h4>
                                                <button
                                                    type="button"
                                                    className="btn-add-item-table"
                                                    onClick={handleAddEditItem}
                                                    disabled={editLoading}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    <span>{translations.addProduct || 'Add Product'}</span>
                                                </button>
                                            </div>
                                            <div className="table-wrapper">
                                                <table className="items-table edit-items-table">
                                                    <thead>
                                                        <tr>
                                                            <th>{translations.productName || 'Product Name'}</th>
                                                            <th>{translations.batchNo || 'Batch No'}</th>
                                                            <th>{translations.unit || 'Unit'}</th>
                                                            <th>{translations.rolls || 'Rolls'}</th>
                                                            <th>{translations.quantity || 'Qty'}</th>
                                                            <th>{translations.price || 'Price'}</th>
                                                            <th>{translations.total || 'Total'}</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {editFormData.items.map((item, index) => {
                                                            const totalAmount = (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);
                                                            return (
                                                                <tr key={item.id || index}>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="text"
                                                                                value={item.product_name || item.productName || ''}
                                                                                onChange={(e) => handleEditItemChange(index, 'product_name', e.target.value)}
                                                                                placeholder={translations.productName || 'Product Name'}
                                                                                disabled={editLoading}
                                                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="text"
                                                                                value={item.batch_no || ''}
                                                                                onChange={(e) => handleEditItemChange(index, 'batch_no', e.target.value)}
                                                                                placeholder={translations.batchNo || 'Batch No'}
                                                                                disabled={editLoading}
                                                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="text"
                                                                                value={item.unit || ''}
                                                                                onChange={(e) => handleEditItemChange(index, 'unit', e.target.value)}
                                                                                placeholder={translations.unit || 'Unit'}
                                                                                disabled={editLoading}
                                                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="number"
                                                                                value={item.rolls || 0}
                                                                                onChange={(e) => handleEditItemChange(index, 'rolls', parseInt(e.target.value) || 0)}
                                                                                placeholder={translations.rolls || 'Rolls'}
                                                                                disabled={editLoading}
                                                                                step="1"
                                                                                min="0"
                                                                                dir="ltr"
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="number"
                                                                                value={item.quantity}
                                                                                onChange={(e) => handleEditItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                                                placeholder={translations.quantity || 'Qty'}
                                                                                className={editFormErrors[`quantity_${index}`] ? 'error' : ''}
                                                                                disabled={editLoading}
                                                                                step="0.01"
                                                                                dir="ltr"
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <input
                                                                                type="number"
                                                                                value={item.price}
                                                                                onChange={(e) => handleEditItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                                                                                placeholder={translations.price || 'Price'}
                                                                                disabled={editLoading}
                                                                                step="0.01"
                                                                                dir="ltr"
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="table-field">
                                                                            <span className="total-amount">{editFormData.currency_symbol || 'Rs'} {totalAmount.toFixed(2)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="table-remove-btn"
                                                                            onClick={() => handleRemoveEditItem(index)}
                                                                            disabled={editLoading || editFormData.items.length === 1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        {editFormErrors.items && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.items}</div>}
                                    </div>

                                    {/* Payment Summary Section */}
                                    <div className="form-section payment-summary-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faMoneyBillWave} />Payment Summary</h4>
                                        <div className="form-grid edit-form-grid">
                                            <div className={`form-group ${editFormErrors.grand_total ? 'has-error' : ''}`}>
                                                <label htmlFor="edit_grand_total">{translations.grandTotal || 'Grand Total'} <span className="required">*</span></label>
                                                <input
                                                    type="number"
                                                    id="edit_grand_total"
                                                    name="grand_total"
                                                    value={editFormData.grand_total}
                                                    onChange={handleEditFormChange}
                                                    required
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                    disabled={editLoading}
                                                    dir="ltr"
                                                />
                                                {editFormErrors.grand_total && (
                                                    <div className="error-message">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        {editFormErrors.grand_total}
                                                    </div>
                                                )}
                                            </div>

                                            <div className={`form-group ${editFormErrors.paid_amount ? 'has-error' : ''}`}>
                                                <label htmlFor="edit_paid_amount">{translations.paidAmount || 'Paid Amount'} <span className="required">*</span></label>
                                                <input
                                                    type="number"
                                                    id="edit_paid_amount"
                                                    name="paid_amount"
                                                    value={editFormData.paid_amount}
                                                    onChange={handleEditFormChange}
                                                    required
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                    disabled={editLoading}
                                                    dir="ltr"
                                                />
                                                {editFormErrors.paid_amount && (
                                                    <div className="error-message">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        {editFormErrors.paid_amount}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="edit_discount">{translations.discount || 'Discount'}</label>
                                                <input
                                                    type="number"
                                                    id="edit_discount"
                                                    name="discount"
                                                    value={editFormData.discount}
                                                    onChange={handleEditFormChange}
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                    disabled={editLoading}
                                                    dir="ltr"
                                                />
                                            </div>

                                            <div className="form-group full-width">
                                                <div className="summary-row">
                                                    <div className="summary-item">
                                                        <span className="summary-label">{translations.balance || 'Balance'}:</span>
                                                        <span className={`summary-value balance ${parseFloat(editFormData.balance) > 0 ? 'negative' : 'positive'}`}>
                                                            {editFormData.currency_symbol || 'Rs'} {parseFloat(editFormData.balance || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="edit-product-info">
                                        <p><strong>{translations.saleId || 'Sale ID'}:</strong> #{selectedSale.id}</p>
                                        <p><strong>{translations.billNumber || 'Bill No'}:</strong> {selectedSale.bill_number}</p>
                                        <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                                        <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedSale.username || user?.username}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editLoading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations.cancel || 'Cancel'}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                    <p className="loading-subtext">Please wait while we update sale data</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this sale?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SellList;